/**
 * よくある質問に答える無人チャットが返す答えの正本。
 *
 * **ここに書いた文をそのまま返す。** モデルに言い換えさせない。
 * 料金や返金を言い換えると事実と違う数字が出るからで、答えを 1 か所に
 * 持っておけば、値上げや文言変更のときにここだけ直せば全部直る。
 *
 * 中身は yumesaku.tech に実際に書いてあることだけ。出どころは各 Q の
 * `source` に残してある。書いていないことは足さない。分からない数字が
 * ある Q は、埋めずに落とす。
 *
 * ## 出どころが割れているところ
 *
 * 旧ヘルプの頁 (2026-10-21 に消した) の「支払い」「ピックアップ」には、/code/pricing と食い違う
 * 記述が残っている (Super プラン・7 日間の無料トライアル・Gemini の
 * AI チューター)。**金額と期間は /code/pricing と LP を正とした。**
 * 旧ヘルプからは、pricing と衝突しない項目 (返金・支払い手段・必要な機材・
 * 1 日の学習時間・学習履歴の保存) だけを取っている。
 *
 * ## 書き方の決まり
 *
 * - `answer` に半角コロンを書かない (機械で見張っている)
 * - URL は `answer` の文中ではなく `links` に置く
 * - `paraphrases` は「同じことを聞く別の言い方」。当てる仕組みはここを見る
 */

export type FaqAnswerLink = {
  label: string;
  /** yumesaku.tech の中のパス。外へ出さない */
  href: string;
};

export type FaqAnswer = {
  id: string;
  /** 代表の聞き方。画面の見出しにもなる */
  question: string;
  /** そのまま返す答え。半角コロン禁止 */
  answer: string;
  /** 同じことを聞く別の言い方。当てる仕組みが照合する */
  paraphrases: string[];
  /** 関連する Q の id。答えたあとに続けて出す */
  related: string[];
  /** 詳しく読むための行き先 */
  links: FaqAnswerLink[];
  /** どのページから取ったか。直すときにここを見る */
  source: string;
};

export const FAQ_ANSWERS: FaqAnswer[] = [
  {
    id: "human-support",
    question: "担当者に相談できますか？",
    answer: "担当者へのお問い合わせは、お問い合わせページのフォームから送信できます。チャットの内容は自動送信されません。相談したい内容をご自身で確認してから送信してください。",
    paraphrases: ["担当者に相談したい", "人に相談したい", "有人対応", "お問い合わせ先", "スタッフと話したい"],
    related: ["ai-tutor", "environment"],
    links: [{ label: "担当者へのお問い合わせ", href: "/contact" }],
    source: "/contact",
  },
  {
    id: "pricing-plans",
    question: "料金はいくらですか",
    answer:
      "定期払いの有料プランは 3 つあります。月額プランが 6,980円、半年プランが 34,800円（月あたり 5,800円・約17%オフ）、年間プランが 54,800円（月あたり約 4,567円・約35%オフ）です。3 つとも使えるコースの範囲は同じで、違うのは支払う周期だけです。半年プランと年間プランは買い切りではなく、解約されない限り同じ期間で自動更新されます。別途、買い切りの生涯プラン（148,000円・税込）もあります。",
    paraphrases: [
      "月額いくらですか",
      "毎月の料金はいくらですか",
      "月額料金を知りたい",
      "値段を教えてください",
      "費用はどれくらいかかりますか",
      "有料プランの価格",
      "サブスクの金額",
      "半年プランと年間プランの違い",
      "プランは何種類ありますか",
      "いくら払うことになりますか",
      "料金プラン",
      "一ヶ月あたりいくら",
    ],
    related: ["free-trial", "free-tier", "pricing-lifetime", "plan-change"],
    links: [{ label: "料金プランを見る", href: "/code/pricing" }],
    source: "https://yumesaku.tech/code/pricing と https://yumesaku.tech の 07 Pricing",
  },
  {
    id: "pricing-lifetime",
    question: "生涯プランはありますか",
    answer:
      "あります。一度きりのお支払い 148,000円（税込）で、有料プランと同じ範囲をずっと使えます。毎月の請求も更新もなく、解約という手続きそのものがありません。今後追加されるコースも含めて全コースが使えます。ゆめさくコードのサービス提供が続く限りご利用いただけます。譲渡はできず、他プランからの差額での乗り換えには対応していません。",
    paraphrases: [
      "買い切りのプランはありますか",
      "買い切り",
      "一度だけ払って使い続けたい",
      "ライフタイムプランの値段",
      "永久に使えるプランはある",
      "更新なしで払い切りたい",
      "生涯プランの金額",
      "サブスクをやめて一括で買いたい",
    ],
    related: ["pricing-plans", "plan-change"],
    links: [{ label: "生涯プランの説明", href: "/code/pricing" }],
    source: "https://yumesaku.tech/code/pricing",
  },
  {
    id: "free-trial",
    question: "無料トライアルはありますか",
    answer:
      "月額プラン・半年プラン・年間プランのいずれでも 30 日間つきます。トライアル中は全コースを利用できます。開始にはプランの選択とクレジットカードの登録が必要です。30 日後、選んだプランの金額（月額プラン 6,980円 / 半年プラン 34,800円 / 年間プラン 54,800円）で課金が始まります。トライアルの終了日と初回請求額は、お申し込み完了画面と支払い管理で確認できます。その間に解約すれば請求はありません。",
    paraphrases: [
      "お試し期間はどれくらいですか",
      "30日間無料と書いてあるけど本当ですか",
      "30日を過ぎるとどうなりますか",
      "自動で課金されますか",
      "いつから課金が始まりますか",
      "無料期間中にやめたら請求されますか",
      "トライアルにカード登録は必要ですか",
      "いつからお金がかかりますか",
      "体験してから決めたい",
      "無料期間はいつまでですか",
    ],
    related: ["pricing-plans", "free-tier", "cancel"],
    links: [{ label: "無料トライアルの条件", href: "/code/pricing" }],
    source: "https://yumesaku.tech/code/pricing の「無料トライアルについて」",
  },
  {
    id: "free-tier",
    question: "無料でどこまで使えますか",
    answer:
      "無料で最後まで読めるコースと、最初の数レッスンを無料で読めるコースがあります。Python・JavaScript・Java・SQL の入門コースは 0円・クレジットカードの登録なしで最後まで読めます。無料枠ではコードの提出と自動採点が 1 日 5 回までで、上限は日本時間の日付が変わると戻ります。有料プランでは全コースを利用できます。",
    paraphrases: [
      "お金をかけずに使えますか",
      "無料枠の範囲を教えてください",
      "無料で読めるコースはどれですか",
      "カード登録なしで始められますか",
      "タダで読めるコースはどれですか",
      "課金しないと何ができませんか",
      "課金しないで使いたい",
    ],
    related: ["pricing-plans", "free-trial", "signup", "languages"],
    links: [
      { label: "無料での登録", href: "/signup" },
      { label: "コースの範囲を見る", href: "/code/pricing" },
    ],
    source: "https://yumesaku.tech/code/pricing と https://yumesaku.tech の FAQ",
  },
  {
    id: "signup",
    question: "どうやって始めればいいですか",
    answer:
      "無料での登録から始められます。クレジットカードは不要で、登録は 30 秒で終わります。入門コースは登録だけで読めるので、続きが必要になった時点でプランを選べます。",
    paraphrases: [
      "登録の仕方を教えてください",
      "はじめかた",
      "はじめるにはどうすればいいですか",
      "始め方を知りたい",
      "アカウントはどう作りますか",
      "新規登録のやり方",
      "会員登録は無料ですか",
      "始めるのに何が必要ですか",
      "申し込み方法",
    ],
    related: ["free-tier", "free-trial", "beginner"],
    links: [{ label: "無料で登録する", href: "/signup" }],
    source: "https://yumesaku.tech の CTA と https://yumesaku.tech/code/pricing",
  },
  {
    id: "cancel",
    question: "解約はいつでもできますか",
    answer:
      "ヘッダーのユーザーメニュー「支払い管理」からいつでも手続きできます。違約金は 0円です。解約手続き後も、契約期間が終了するまでは引き続き利用できます。契約期間の終了後は有料コンテンツが利用できなくなりますが、無料枠と学習履歴はそのまま残ります。",
    paraphrases: [
      "やめたいときはどうしますか",
      "解約方法",
      "やめるときの手続き",
      "途中で解約できますか",
      "サブスクを止めたい",
      "違約金はかかりますか",
      "キャンセルの方法を教えてください",
      "解約したらデータは消えますか",
      "退会したい",
    ],
    related: ["free-trial", "refund", "progress"],
    links: [{ label: "解約について", href: "/code/pricing" }],
    source: "https://yumesaku.tech/code/pricing と https://yumesaku.tech の FAQ",
  },
  {
    id: "refund",
    question: "返金はできますか",
    answer:
      "原則として、一度お支払いいただいた料金の返金は行っておりません。ただし、法令により返金が義務付けられている場合や、当社が特別に認めた場合はこの限りではありません。",
    paraphrases: [
      "払ったお金は戻ってきますか",
      "返金してほしい",
      "返金対応はありますか",
      "返金保証はついていますか",
      "お金は戻りますか",
      "途中でやめたら日割りで返してもらえますか",
    ],
    related: ["cancel", "free-trial", "payment-method"],
    links: [{ label: "料金プランを見る", href: "/code/pricing" }],
    source: "旧ヘルプの頁の「支払い」",
  },
  {
    id: "payment-method",
    question: "支払い方法は何がありますか",
    answer:
      "クレジットカード（Visa、Mastercard、JCB、American Express）でのお支払いに対応しています。決済は Stripe を通じて安全に処理されます。",
    paraphrases: [
      "使えるカードを教えてください",
      "どうやって払いますか",
      "支払いに使えるものは何ですか",
      "クレジットカード以外で払えますか",
      "銀行振込はできますか",
      "コンビニ払いに対応していますか",
      "決済手段は何ですか",
      "デビットカードは使えますか",
    ],
    related: ["pricing-plans", "refund"],
    links: [{ label: "料金プランを見る", href: "/code/pricing" }],
    source: "旧ヘルプの頁の「支払い」",
  },
  {
    id: "plan-change",
    question: "あとからプランを変えられますか",
    answer:
      "解約予約中でない定期払いの契約は、支払い管理からプランを変更できます。トライアル中も課金後も、半年プラン・年間プランへ変更していただけます。",
    paraphrases: [
      "月額から年間に切り替えたい",
      "プランを変えたい",
      "年間プランに変更したい",
      "プランの切り替えはできますか",
      "途中でプラン変更はできますか",
      "あとで安いプランに移れますか",
      "アップグレードの方法",
    ],
    related: ["pricing-plans", "pricing-lifetime", "annual-credit"],
    links: [{ label: "料金プランを見る", href: "/code/pricing" }],
    source: "https://yumesaku.tech/code/pricing",
  },
  {
    id: "annual-credit",
    question: "年間プランの年会費はスクールで使えますか",
    answer:
      "年間プラン会員の方は、ゆめさくエンジニア入学時に年会費全額（54,800円）を受講料に充当します。充当は入学時 1 回限りで、現金での還付はありません。",
    paraphrases: [
      "年会費を受講料に回せますか",
      "年会費の充当について",
      "年間プランの年会費はどうなりますか",
      "スクールに入るとき年間プランの分はどうなりますか",
      "年間プランの特典を教えてください",
    ],
    related: ["pricing-plans", "school-women", "student-free"],
    links: [{ label: "年間プランの条件", href: "/code/pricing" }],
    source: "https://yumesaku.tech/code/pricing の注記",
  },
  {
    id: "student-free",
    question: "スクールの受講生も課金が必要ですか",
    answer:
      "ゆめさくエンジニア Women を受講している間は、ゆめさくコードの有料プランと同じ範囲を追加料金なしで使えます。お申し込みは必要ありません。ご利用方法はスクールからご案内します。",
    paraphrases: [
      "受講生特典はありますか",
      "スクール在籍中の料金はいくらですか",
      "在籍中に追加料金はかかりますか",
      "スクールに通いながらコードも使えますか",
      "ゆめさくエンジニアの生徒はコードも使えますか",
      "スクールの生徒は無料で使えますか",
    ],
    related: ["school-women", "pricing-plans", "annual-credit"],
    links: [{ label: "スクールを見てみる", href: "/women" }],
    source: "https://yumesaku.tech/code/pricing の「受講生の方へ」",
  },
  {
    id: "beginner",
    question: "完全未経験でも大丈夫ですか",
    answer:
      "はい。最初のレッスンは print('Hello') の一行から始まり、1 レッスン 5 分で完結します。タイピング未経験の社会人や文系大学生も、初日からコードが動く体験ができます。",
    paraphrases: [
      "初心者でもついていけますか",
      "初めてでも大丈夫ですか",
      "初心者向けですか",
      "経験がなくても理解できますか",
      "プログラミングをやったことがありません",
      "文系でも平気ですか",
      "パソコンが苦手でも学べますか",
      "未経験ですが始められますか",
    ],
    related: ["study-time", "ai-tutor", "learning-order", "signup"],
    links: [{ label: "コースを見る", href: "/learn/courses" }],
    source: "https://yumesaku.tech の FAQ",
  },
  {
    id: "environment",
    question: "環境構築は必要ですか",
    answer:
      "環境構築はいりません。ブラウザでそのままコードを書けて、書いたコードはその場で動きます。Linux はターミナルごと入っていて、SQL は実際のテーブルを叩けます。",
    paraphrases: [
      "インストールするものはありますか",
      "インストールは必要ですか",
      "準備するものはありますか",
      "自分でセットアップしないとダメですか",
      "開発環境の準備は要りますか",
      "ブラウザだけで学習できますか",
    ],
    related: ["device", "beginner", "languages"],
    links: [{ label: "学習画面を見る", href: "/" }],
    source: "https://yumesaku.tech の 02 Screens と比較表",
  },
  {
    id: "device",
    question: "学習に必要な機材は何ですか",
    answer:
      "インターネットに接続できるパソコンまたはタブレットが必要です。推奨ブラウザは Google Chrome、Safari、Firefox の最新版です。スマートフォンでも閲覧はできますが、コーディング学習にはパソコンをおすすめします。",
    paraphrases: [
      "スマホだけで勉強できますか",
      "スマートフォンでも学習できますか",
      "対応ブラウザを教えてください",
      "どのブラウザで使えますか",
      "必要な機材は何ですか",
      "MacでもWindowsでも大丈夫ですか",
      "パソコンは必要ですか",
    ],
    related: ["environment", "beginner"],
    links: [{ label: "お問い合わせ", href: "/contact" }],
    source: "旧ヘルプの頁の「ピックアップ」",
  },
  {
    id: "languages",
    question: "どんな言語が学べますか",
    answer:
      "HTML・CSS、Python、JavaScript、TypeScript、Java、SQL、React、Next.js、Linux が学べます。ほかに生成AI、コンピューターサイエンス、資格対策のコースもあります。",
    paraphrases: [
      "扱っているプログラミング言語を教えてください",
      "学べる言語を教えてください",
      "Pythonはありますか",
      "JavaScriptのコースはありますか",
      "SQLは学べますか",
      "Reactも学べますか",
      "コースの種類を知りたい",
      "何が勉強できますか",
      "AIについて学べますか",
    ],
    related: ["learning-order", "lesson-count", "free-tier"],
    links: [{ label: "コース一覧", href: "/learn/courses" }],
    source: "https://yumesaku.tech/code/pricing のコース表と https://yumesaku.tech の「言語から探す」",
  },
  {
    id: "lesson-count",
    question: "レッスンは何本ありますか",
    answer: "レッスンは 2,264 本あります。",
    paraphrases: [
      "教材のボリュームはどれくらいですか",
      "レッスンの数はどれくらいですか",
      "何レッスンありますか",
      "全部で何レッスンですか",
      "コンテンツの量を教えてください",
    ],
    related: ["languages", "learning-order", "study-time"],
    links: [{ label: "レッスン一覧", href: "/learn/lessons" }],
    source: "https://yumesaku.tech と https://yumesaku.tech/code/pricing の表示",
  },
  {
    id: "learning-order",
    question: "何から学べばいいですか",
    answer:
      "学ぶ順番は決まっています。たとえば HTML・CSS なら、HTML入門、CSS入門、CSSレイアウト、レスポンシブWeb制作の順に進みます。ほかのコースも同じように順番が決まっているので、何から手を付けるかを迷わせません。",
    paraphrases: [
      "学習の順序はどうなっていますか",
      "どの順番で進めればいいですか",
      "進める順番は決まっていますか",
      "カリキュラムの流れを知りたい",
      "順番を自分で決めないといけませんか",
      "ロードマップはありますか",
      "最初にやるべきコースはどれですか",
      "どういう流れで進みますか",
    ],
    related: ["languages", "beginner", "study-time"],
    links: [
      { label: "コース一覧", href: "/learn/courses" },
    ],
    source: "https://yumesaku.tech の Roadmap 節",
  },
  {
    id: "study-time",
    question: "1日どのくらい学習すればいいですか",
    answer:
      "1 レッスン約 5 分で設計しているため、1 日 5 分からでも学習できます。続けることが大切なので、無理のないペースで進めることをおすすめします。",
    paraphrases: [
      "忙しくても続けられますか",
      "1日どのくらい時間が要りますか",
      "学習時間はどのくらいですか",
      "1レッスンの長さは",
      "仕事をしながらでも学べますか",
    ],
    related: ["beginner", "learning-order", "lesson-count"],
    links: [{ label: "コースを見る", href: "/learn/courses" }],
    source: "旧ヘルプの頁の「学習の方法」と https://yumesaku.tech の FAQ",
  },
  {
    id: "ai-tutor",
    question: "分からないところは質問できますか",
    answer:
      "AI先生「Tap」がエラー画面のスクショ付きで 24 時間質問を受け付けます。なぜ動かないのかをその場で解消できます。有料プランでは Tap とのやり取りが無制限です。",
    paraphrases: [
      "つまずいたときは誰に聞けますか",
      "質問できる相手はいますか",
      "AIに聞けますか",
      "エラーで詰まったときに助けはありますか",
      "AI先生に聞けますか",
      "エラーが出たらどうすればいいですか",
      "挫折しそうなときの支えはありますか",
    ],
    related: ["beginner", "pricing-plans", "environment"],
    links: [{ label: "コースを見る", href: "/learn/courses" }],
    source: "https://yumesaku.tech の FAQ と https://yumesaku.tech/code/pricing",
  },
  {
    id: "progress",
    question: "学習の進み具合は保存されますか",
    answer:
      "ログインしている状態での学習進捗やコードはすべてサーバーに保存されます。別のデバイスからアクセスしても、続きから学習を再開できます。解約したあとも学習履歴はそのまま残ります。",
    paraphrases: [
      "途中でやめても続きから再開できますか",
      "学習の記録は残りますか",
      "書いたコードは残りますか",
      "データは保存されますか",
      "別のパソコンからでも同じ状態で使えますか",
      "進捗は記録されますか",
    ],
    related: ["cancel", "study-time"],
    links: [{ label: "コースを見る", href: "/learn/courses" }],
    source: "旧ヘルプの頁の「学習の方法」と https://yumesaku.tech/code/pricing",
  },
  {
    id: "jobs",
    question: "学んだスキルで仕事は見つかりますか",
    answer:
      "提携の kyujin-erabi.jp で、修了コースに応じた求人を優先紹介します。SQL・Python 修了者は社内データ職、JavaScript 修了者は Web 制作職など、学習履歴を企業に共有できます。",
    paraphrases: [
      "転職につながりますか",
      "就職の支援はありますか",
      "求人の紹介はありますか",
      "仕事は見つかりますか",
      "仕事にできるレベルになりますか",
    ],
    related: ["school-women", "skills", "languages"],
    links: [{ label: "求人を見る", href: "/jobs" }],
    source: "https://yumesaku.tech の FAQ と Next Steps",
  },
  {
    id: "school-women",
    question: "ゆめさくエンジニア Women とは何ですか",
    answer:
      "女性限定・18 ヶ月のスクールです。企業が高い年収を出すのは書ける人ではなく設計から任せられる人だという考えから、設計から任せられる人になることを目指します。ゆめさくコードと同じチームが運営しています。",
    paraphrases: [
      "スクールもやっているのですか",
      "ゆめさくエンジニアとは何ですか",
      "スクールについて知りたい",
      "ゆめさくコードとスクールの違いは何ですか",
      "講師がつくコースはありますか",
      "女性向けのスクールについて知りたい",
    ],
    related: ["student-free", "annual-credit", "jobs"],
    links: [{ label: "ゆめさくエンジニア Women", href: "/women" }],
    source: "https://yumesaku.tech の Next Steps",
  },
  {
    id: "skills",
    question: "作品づくりの課題はありますか",
    answer:
      "ゆめさくスキルがあります。買い切り 49,800円で、実際の案件を教材にした課題を 1 本まるごと作り切るものです。レッスンを終えても作り切った経験は別に要る、という考えで用意しています。",
    paraphrases: [
      "ポートフォリオは作れますか",
      "ポートフォリオ用の課題はありますか",
      "実践的な開発課題はありますか",
      "作品を作る課題はありますか",
      "ゆめさくスキルとは何ですか",
      "案件みたいな課題をやりたい",
    ],
    related: ["jobs", "pricing-plans"],
    links: [{ label: "ゆめさくスキルを見る", href: "/skills" }],
    source: "https://yumesaku.tech の Next Steps",
  },
  {
    id: "lab",
    question: "ゆめさくラボとは何ですか",
    answer:
      "AI・IT の学び方と技術を、実際に試して確かめる研究所です。講師インタビュー、受講生インタビュー、学習ガイド、キャリアの記事を公開しています。",
    paraphrases: [
      "ラボには何が載っていますか",
      "ラボとは何ですか",
      "研究所では何をしていますか",
      "インタビュー記事は読めますか",
      "受講生インタビューは読めますか",
      "講師のことを知りたい",
      "記事はありますか",
    ],
    related: ["school-women", "learning-order"],
    links: [
      { label: "ゆめさくラボ", href: "/lab" },
      { label: "記事一覧", href: "/lab/articles" },
    ],
    source: "https://yumesaku.tech/lab",
  },
];

/**
 * 当たらなかったときに出す行き先。
 *
 * **人へ転送する口ではない。** どちらも yumesaku.tech の中のページで、
 * 無人のまま自分で探せるところへ送るだけ。LINE を出すかどうかは
 * 未決 (公式アカウントの URL を site 側で確認できていない)。
 */
export const FAQ_FALLBACK_LINKS: FaqAnswerLink[] = [
  { label: "よくある質問", href: "/lab/faq" },
  { label: "お問い合わせ", href: "/contact" },
];

/** 当たらなかったときに返す文。ここも言い換えさせない */
export const FAQ_UNKNOWN_MESSAGE =
  "その質問の答えは持っていません。分かることだけを答えるようにしているので、確かめられていないことは書きません。下のページから探してみてください。";

export const FAQ_ANSWER_BY_ID: ReadonlyMap<string, FaqAnswer> = new Map(
  FAQ_ANSWERS.map((entry) => [entry.id, entry]),
);

/** 関連 Q を実体で引く。id が壊れていたら黙って落とす */
export function relatedFaqAnswers(entry: FaqAnswer): FaqAnswer[] {
  return entry.related
    .map((id) => FAQ_ANSWER_BY_ID.get(id))
    .filter((found): found is FaqAnswer => found !== undefined);
}
